import type { RefinementCtx } from 'zod';

/**
 * IL/ILCE LISTESI (2026-10-02, M4) — formlarin secicileri icin saf yardimcilar.
 *
 * Kaynak sunucu: GET iam /v1/locations (81 il / 973 ilce, resmi yazim). Il/ilce eskiden alti
 * formda serbest metindi; artik listeden secilir. Kolonlarda AD saklanir (kod yok), yani secilen
 * deger dogrudan resmi yazimdir.
 */
export type TrLocation = { name: string; districts: readonly string[] };

const asTrimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const isText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

/** Sunucu yanitini savunmaci bicimde okur: bozuk satir sessizce atlanir, liste bos olabilir. */
export const parseLocations = (raw: unknown): TrLocation[] =>
  Array.isArray(raw)
    ? raw.flatMap((item: unknown) => {
        if (!item || typeof item !== 'object') return [];
        const { name, districts } = item as {
          name?: unknown;
          districts?: unknown;
        };
        if (!isText(name) || !Array.isArray(districts)) return [];
        return [{ name, districts: districts.filter(isText) }];
      })
    : [];

export const districtsOf = (locations: readonly TrLocation[], city: string): readonly string[] =>
  locations.find((location) => location.name === city)?.districts ?? [];

export const isListedCity = (locations: readonly TrLocation[], city: string): boolean =>
  locations.some((location) => location.name === city);

/**
 * Form semasina eklenen il/ilce dogrulamasi (`schema.superRefine(...)`). Liste henuz yuklenmediyse
 * (bos) hicbir sey yapmaz: sunucu yazilan degeri yine listeye esitliyor. Bos deger burada
 * yakalanmaz — zorunluluk alanin kendi semasinda.
 */
export const refineLocation =
  ({
    locations,
    cityField,
    districtField,
    message,
  }: {
    locations: readonly TrLocation[];
    cityField: string;
    districtField: string;
    message: string;
  }) =>
  (values: Record<string, unknown>, ctx: RefinementCtx): void => {
    if (locations.length === 0) return;
    const city = asTrimmed(values[cityField]);
    const district = asTrimmed(values[districtField]);
    if (city !== '' && !isListedCity(locations, city)) {
      ctx.addIssue({ code: 'custom', path: [cityField], message });
      return;
    }
    if (district !== '' && !districtsOf(locations, city).includes(district)) {
      ctx.addIssue({ code: 'custom', path: [districtField], message });
    }
  };
